/**
 * CredentialsEdit — edit form for an existing credential.
 *
 * Epic 3 (Slice 3.2): mounts `CredentialStatusCard` directly above the
 * credential form card for dashboard-eligible credentials.
 *  - Recovery actions route into existing §37e (HarvestModal) and §37g
 *    (RecreateApiKeyInstructionalPanel) — not rebuilt here.
 *  - The "Test connection" action lives inside `CredentialStatusCard`.
 *
 * §38.6: workflow-only credentials (no `connectionStatus`) render no card —
 * the form starts at the first field.
 *
 * Brand guide §38.5. TDD §"New / Modified Pages or Components". Slice 3.2.
 */

import { useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { AppDispatch, RootState } from "@/redux"
import { CredentialsAppHeader } from "../CredentialsAppHeader/CredentialsAppHeader"
import { LoginMethod, CredentialDetailsResponseData } from "@/types/credentials"
import { CredentialsAPI } from "@/API/Credentials"
import { appActions } from "@/redux/slice/appSlice"
import { getRespectiveForm } from '../helpers/getRespectiveForm'
import { CredentialMethod } from "@/constants/credentials/credential"
import { AppName } from "@/constants/application/application"
import { useParams } from "react-router-dom"
import { ApplicationsAPI } from "@/API/Applications"
import { showNotification } from "@/services/notificationService"
import { CredentialStatusCard } from "../CredentialStatusCard"
import { HarvestModal } from '@/pages/Dashboards/components/harvest/HarvestModal'
import { RecreateApiKeyInstructionalPanel } from '@/pages/Dashboards/components/creation/RecreateApiKeyInstructionalPanel'
import { startHarvestThunk } from '@/services/HarvestService'
import { dashboardCreationActions, selectHarvestFailure } from '@/redux/slice/dashboardCreationSlice'
import { getCredentials } from "@/services/credentialsService"

export const CredentialsEdit = () => {
  const { credId: credentialId } = useParams()
  const hasFetchedRef = useRef(false)
  const dispatch: AppDispatch = useDispatch()
  const { token } = useSelector((state: RootState) => state.user)
  const [loginMethods, setLoginMethods] = useState<LoginMethod[]>([])
  const [credentialDetails, setCredentialDetails] = useState<CredentialDetailsResponseData | undefined>(undefined)
  const harvestFailure = useSelector(selectHarvestFailure)
  const { pagination } = useSelector((state: RootState) => state.credentials)

  // §37e Reconnect modal state (invalid_key recovery — HarvestModal)
  const [isHarvestModalOpen, setIsHarvestModalOpen] = useState(false)

  // §37g Fix access panel state (missing_permissions recovery)
  const [isFixAccessOpen, setIsFixAccessOpen] = useState(false)

  // Look up the credential from the list Redux state to access connectionStatus.
  // The list carries the eligibility signal (connectionStatus) from the BE, which
  // the credential-details DTO does NOT include. On a deep-link / refresh straight
  // to the edit URL the list can be empty; the effect below backfills it via
  // getCredentials so a dashboard-eligible credential still renders its status card
  // (rather than silently hiding it). getCredentials uses the admin no-probe path —
  // it never opts into withAvailabilityProbe, so no live probe fires on edit load.
  const credentialFromList = useSelector((state: RootState) =>
    state.credentials.credentials.find(c => c.id === credentialId)
  )
  const listIsEmpty = useSelector(
    (state: RootState) => state.credentials.credentials.length === 0
  )

  useEffect(() => {
    if (token && credentialId && !hasFetchedRef.current) {
      hasFetchedRef.current = true
      dispatch(appActions.setLoading(true))
      const fetchCredentialDetails = async () => {
        const resp = await CredentialsAPI.getCredentialDetails(
          token,
          credentialId
        )
        if (resp.data) {
          setCredentialDetails(resp.data)
          const loginMethodsResp = await ApplicationsAPI.getLoginMethodsOfApp(
            token,
            resp.data?.app.id
          )
          if (loginMethodsResp.data) {
            setLoginMethods(loginMethodsResp.data.loginMethods)
          }
        } else {
          showNotification({ data: undefined, err: resp.err }, '', {})
        }
      }
      fetchCredentialDetails()
        .then(() => dispatch(appActions.setLoading(false)))
        .catch(() => dispatch(appActions.setLoading(false)))
    }
  }, [token, dispatch, credentialId])

  // Deep-link / refresh backfill: if the list state is empty, the credential row
  // (and its connectionStatus) won't be present. Fetch ALL credentials (sentinel
  // limit) so any credential ID resolves regardless of page position — a paginated
  // fetch would miss credentials beyond page 1. No-op when the list is already
  // loaded. Admin no-probe path — withAvailabilityProbe never set, zero live probes.
  //
  // TODO(backlog): the clean fix is a new BE change adding `connectionStatus` to
  // `GET /v1/credentials/:id` so the edit page can fetch its own credential by id
  // and drop the list dependency entirely (the existing details fetch already exists).
  const BACKFILL_LIMIT = 10_000
  useEffect(() => {
    if (token && listIsEmpty) {
      void dispatch(getCredentials(token, BACKFILL_LIMIT, 0))
    }
  }, [token, listIsEmpty, dispatch])

  // Connection status from the list state (undefined for workflow-only credentials).
  const connectionStatus = credentialFromList?.connectionStatus

  // §37e: Reconnect (invalid_key) — opens the HarvestModal to re-enter/harvest a key.
  const handleReconnect = async () => {
    if (!token) return
    dispatch(dashboardCreationActions.clearHarvest())
    await (dispatch as (action: unknown) => Promise<void>)(
      startHarvestThunk(token, 'campminder')
    )
    setIsHarvestModalOpen(true)
  }

  // Close HarvestModal if harvest fails mid-flow (mirrors Credentials.tsx:73-77 useEffect pattern).
  useEffect(() => {
    if (harvestFailure && isHarvestModalOpen) {
      setIsHarvestModalOpen(false)
    }
  }, [harvestFailure, isHarvestModalOpen])

  const handleHarvestClose = () => {
    setIsHarvestModalOpen(false)
    dispatch(dashboardCreationActions.clearHarvest())
  }

  const handleHarvestSuccess = () => {
    setIsHarvestModalOpen(false)
    // Refresh credentials list so the status card reflects the new key.
    if (token) {
      void dispatch(getCredentials(token, pagination.limit, pagination.offset))
    }
  }

  // §37g: Fix access (missing_permissions) — opens the instructional panel.
  const handleFixAccess = () => {
    setIsFixAccessOpen(true)
  }

  const handleFixAccessClose = () => {
    setIsFixAccessOpen(false)
  }

  const handleFixAccessSuccess = () => {
    setIsFixAccessOpen(false)
    // Refresh credentials list so the status card reflects the updated key.
    if (token) {
      void dispatch(getCredentials(token, pagination.limit, pagination.offset))
    }
  }

  return (
    <div className="protectedPageLayout">
      {credentialId && (
        <CredentialsAppHeader
          method='Edit'
          appName={credentialDetails?.app.displayName ?? ""}
          iconUrl={credentialDetails?.app.logo ?? ""}
        />
      )}

      {/* §38.5 Status card — sits above the form card.
          Renders nothing for workflow-only credentials (§38.6 not-applicable). */}
      {credentialId && (
        <CredentialStatusCard
          credentialId={credentialId}
          connectionStatus={connectionStatus}
          onReconnect={() => { void handleReconnect() }}
          onFixAccess={handleFixAccess}
        />
      )}

      {credentialDetails && loginMethods && getRespectiveForm(
        credentialDetails.app.name as AppName,
        credentialDetails.app.id,
        loginMethods,
        CredentialMethod.EDIT,
        false,
        credentialDetails
      )}

      {/* §37e: HarvestModal for Reconnect (invalid_key recovery) */}
      <HarvestModal
        isOpen={isHarvestModalOpen}
        onClose={handleHarvestClose}
        onSuccess={handleHarvestSuccess}
        onPasteKey={handleHarvestClose}
        advanceCreationFlowOnSuccess={false}
      />

      {/* §37g: RecreateApiKeyInstructionalPanel for Fix access (missing_permissions recovery) */}
      {isFixAccessOpen && credentialId && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          role="dialog"
          aria-modal="true"
          aria-label="Fix credential access"
        >
          <div className="bg-card rounded-xl shadow-2xl w-[380px] max-h-[90vh] overflow-y-auto">
            <RecreateApiKeyInstructionalPanel
              credentialId={credentialId}
              credentialLabel={credentialFromList?.nickname ?? credentialDetails?.nickname ?? 'this credential'}
              sourceAppCode="campminder"
              onClose={handleFixAccessClose}
              onSuccess={handleFixAccessSuccess}
            />
          </div>
        </div>
      )}
    </div>
  )
}
